import React, { useEffect, useState } from "react";
import { LuRefreshCw } from "react-icons/lu";
import { Button, Dialog, Field, Skeleton, Spinner, Textarea, cn } from "../../components/ui";
import { StepsBar } from "../../components/patterns/StepsBar";
import { t } from "../../i18n";

const MAX_CHARS = 900;

interface SummaryDescriptionDialogProps {
  /** Texto sugerido pela IA (`descricao_resumida`). `null` enquanto gera. */
  suggestion: string | null;
  loading: boolean;
  /** A geração falhou (502 / rede). */
  error: boolean;
  onRegenerate: () => void;
  onConfirm: (summary: string) => void;
  onClose: () => void;
}

/**
 * Passo 1 da guia de internação: a "Descrição do paciente" resumida (até 900
 * caracteres) derivada da completa. O médico revisa, edita e continua para a
 * escolha do plano; a versão completa continua no pedido.
 */
export const SummaryDescriptionDialog: React.FC<SummaryDescriptionDialogProps> = ({
  suggestion,
  loading,
  error,
  onRegenerate,
  onConfirm,
  onClose,
}) => {
  const [text, setText] = useState(suggestion ?? "");

  // Reidrata quando chega uma nova sugestão (primeira geração ou "Gerar de novo").
  useEffect(() => {
    if (suggestion !== null) setText(suggestion);
  }, [suggestion]);

  const overLimit = text.length > MAX_CHARS;
  const canConfirm = !loading && !error && text.trim().length > 0 && !overLimit;

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("guides.summaryTitle")}
      description={<StepsBar step={1} label={t("guides.summaryStep")} />}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" disabled={!canConfirm} onClick={() => onConfirm(text.trim())}>
            {t("guides.continue")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-caption text-fg-muted">{t("guides.summaryHint")}</p>

        {loading && (
          <div role="status" className="flex flex-col gap-2.5 rounded-card border border-line p-card">
            <p className="flex items-center gap-2 text-caption text-fg-subtle">
              <Spinner size={16} />
              {t("guides.generating")}
            </p>
            {["w-[95%]", "w-[88%]", "w-[70%]"].map((w) => (
              <Skeleton key={w} className={cn("h-3.5", w)} />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <p className="text-body text-danger">{t("guides.generateError")}</p>
            <Button variant="primary" size="sm" icon={<LuRefreshCw size={16} aria-hidden />} onClick={onRegenerate}>
              {t("common.retry")}
            </Button>
          </div>
        )}

        {!loading && !error && (
          <>
            <Field
              label={
                <span className="flex items-center justify-between gap-2">
                  {t("guides.summaryLabel")}
                  <span className={cn("text-caption tabular-nums", overLimit ? "font-semibold text-danger" : "text-fg-subtle")}>
                    {text.length}/{MAX_CHARS}
                  </span>
                </span>
              }
              hint={t("guides.summaryReview")}
              error={overLimit ? t("guides.summaryTooLong", { max: MAX_CHARS }) : undefined}
            >
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} className="resize-y leading-relaxed" />
            </Field>
            <Button variant="text" size="sm" className="self-start" icon={<LuRefreshCw size={16} aria-hidden />} onClick={onRegenerate}>
              {t("guides.regenerate")}
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
};
